/**
 * useLanguageChooser Hook
 * Business logic for language selection
 *
 * DDD Layer: Application
 * Responsibilities:
 * - Manage language state
 * - Handle language switching
 * - Provide language options
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { FlagEnIcon, FlagHeIcon } from '../Icons';
import { LanguageOption } from './LanguageSelect';
import { useLayoutDirection } from '../../../providers/layoutDirectionProvider';

export const useLanguageChooser = () => {
  const { i18n } = useTranslation();
  const { toggleLayoutDirection } = useLayoutDirection();

  // Available language options with flag icons
  const languageOptions: LanguageOption[] = useMemo(
    () => [
      {
        value: 'en',
        label: 'English',
        icon: <FlagEnIcon width={20} height={15} />,
      },
      {
        value: 'he',
        label: 'עברית',
        icon: <FlagHeIcon width={20} height={15} />,
      },
    ],
    []
  );

  // Get current language
  const currentLanguage = useMemo(() => {
    return languageOptions.find(option => option.value === i18n.language) || languageOptions[0];
  }, [languageOptions, i18n.language]);

  // Handle language change
  const handleLanguageChange = (selectedOption: SingleValue<LanguageOption>) => {
    if (selectedOption && selectedOption.value !== i18n.language) {
      toggleLayoutDirection(selectedOption.value === 'he');
    }
  };

  return {
    languageOptions,
    currentLanguage,
    handleLanguageChange,
  };
};
